/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import format from 'date-fns/format';
import { Link, useLocation } from 'react-router-dom';

import { RMWContext } from 'app/shared/providers/RMWContext';

import {
    Anchor,
    Button,
    Flex,
    IconHelp,
    IconCalendarOutline,
    PageBreadcrumb,
    Paragraph,
    Spacer,
    Text,
    TextButton,
    Tooltip,
    TooltipPopper,
} from '@zillow/constellation';
import {
    Col,
    Container,
    Row,
} from '@zillow/zrs-comet';
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';

// Constants
import { BILLING_PAGE } from 'app/shared/constants/RouteConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import BillingConstants from 'app/shared/constants/BillingConstants';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

// Components
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import TableMFH from 'app/shared/components/TableMFH';
import BillingError from 'app/shared/components/Billing/BillingError';
import BillingFilter from 'app/shared/components/Billing/BillingFilter';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// Selectors
import {
    AppSelectors,
    RoutingSelectors,
    BillingSelectors,
    UserSelectors,
} from 'app/shared/selectors';

// Utils
import numberUtils from 'app/shared/utils/numberUtils';
import billingUtils from 'app/shared/utils/billingUtils';
import accessUtils from 'app/shared/utils/accessUtils';

import {
    StyledContainer,
    StyledSummaryBox,
} from './StyledComponents';

const propTypes = {
    currentInvoice: PropTypes.object,
    currentInvoiceLoadingState: PropTypes.string,
    currentInvoiceErrorMessage: PropTypes.string,
};

const defaultProps = {
    currentInvoice: null,
    currentInvoiceLoadingState: LoadingConstants.INITIAL,
    currentInvoiceErrorMessage: null,
};

const DEFAULT_PAGE_TITLE = 'Invoice';
const { INVOICE_TYPES } = BillingConstants;

// Stylesheets
import './style.scss';

const formatDate = value => value && format(new Date(value), 'MM/DD/YYYY hh:mm aa');

const mapActiveRanges = (activeRanges) => {
    if (!activeRanges) {
        return '-';
    }
    return activeRanges.split(';').map(range => (
        <div className="InvoicePage-active-range">
            {range}
        </div>
    ));
};

const InvoicePage = ({
    match,
    dispatch,
    currentInvoice,
    currentInvoiceLoadingState,
    currentInvoiceErrorMessage,
    isScreenMobile,
    currentInvoiceFilters,
    currentInvoiceLineItems,
    user,
}) => {
    const location = useLocation();
    const queryParams = ZRMUtils.queryUtils.parse(location.search);
    const isSingleFamilyLegacy = currentInvoice && currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_LEGACY;
    const paymentsEnabled = accessUtils.canAccessInvoicePayments({ user });
    const { paths } = React.useContext(RMWContext);

    const amountHeaderRenderer = () => (isSingleFamilyLegacy ? 'EST. AMOUNT' : 'AMOUNT');

    let invoiceColumnConfig = [
        {
            width: 4,
            header: 'Property',
            dataKey: 'propertyName',
        },
        {
            width: 2,
            header: 'Charge Item',
            dataKey: 'chargeName',
            renderer: (data) => {
                if (data.chargeName.includes('Zillow Feed Connect')) {
                    return (
                        <TooltipPopper
                            mouseEnterDelay={100}
                            mouseLeaveDelay={100}
                            triggered={() => (
                                <Tooltip
                                    body={(
                                        <Paragraph>
                                            Billed per published unit per day.
                                        </Paragraph>
                                    )}
                                />
                            )}
                        >
                            <Text fontType="finePrint">
                                {data.chargeName}
                                <IconHelp size="xs" />
                            </Text>
                        </TooltipPopper>
                    );
                }
                return data.chargeName;
            },
        },
        {
            width: 1,
            header: currentInvoice && (currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_DAILY_LISTINGS ||
                currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_LEGACY)
                ? 'Active Days'
                : 'QTY',
            dataKey: 'quantity',
            align: 'right',
            renderer: (data) => {
                const { quantity, activeRanges } = data;
                if (isNaN(quantity)) return '-';
                if (data.chargeName.includes('Zillow Feed Connect')) {
                    return (
                        <TooltipPopper
                            className="active-days-Tooltip"
                            mouseEnterDelay={100}
                            mouseLeaveDelay={100}
                            triggered={() => (
                                <Tooltip
                                    body={(
                                        <div>
                                            {mapActiveRanges(activeRanges)}
                                        </div>
                                    )}
                                />
                            )}
                        >
                            <div>
                                {quantity % 1 === 0 ? quantity : quantity.toFixed(2)}
                                <IconCalendarOutline size="sm" />
                            </div>
                        </TooltipPopper>
                    );
                }
                return quantity % 1 === 0 ? quantity : quantity.toFixed(2);
            },
        },
        {
            width: 2,
            header: currentInvoice && currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_DAILY_LISTINGS
                ? 'Daily Rate'
                : 'Unit Price',
            dataKey: 'unitPrice',
            renderer: (value) => {
                if (!value || !value.quantity) return '-';
                const formattedDiscount = billingUtils.formatDiscountRate(value);
                const formattedValue = numberUtils.asCurrencyWithDefault(value.chargeAmount / value.quantity, '-');
                return (
                    <span>
                        {formattedValue}
                        {formattedDiscount && (
                            <div>{formattedDiscount}</div>
                        )}
                    </span>
                );
            },
            align: 'right',
        },
        {
            width: 2,
            dataKey: 'chargeAmount',
            headerRenderer: amountHeaderRenderer,
            renderer: (value) => {
                const formattedValue = numberUtils.asCurrency(value.chargeAmount, '$0,0.00');
                const formattedDiscount = billingUtils.formatDiscountAmount(value);
                return (
                    <span>
                        {formattedValue}
                        {formattedDiscount && (
                            <div>{formattedDiscount}</div>
                        )}
                    </span>
                );
            },
            align: 'right',
        },
    ];

    // Hide unit price if single family legacy
    if (currentInvoice && currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_LEGACY) {
        invoiceColumnConfig = invoiceColumnConfig.filter(col => col.dataKey !== 'unitPrice');
    }

    const mobileColumnConfig = [
        {
            width: 4,
            header: 'Property',
            dataKey: 'propertyName',
        },
        {
            width: 4,
            header: 'Product',
            dataKey: 'chargeName',
        },
        {
            width: 3,
            dataKey: 'chargeAmount',
            headerRenderer: amountHeaderRenderer,
            renderer: value => numberUtils.asCurrency(value.chargeAmount, '$0,0.00'),
            align: 'right',
        },
    ];

    const columnConfig = isScreenMobile ? mobileColumnConfig : invoiceColumnConfig;

    const historyColumnConfig = [
        {
            dataKey: 'date',
            width: 3,
            header: 'Payment Date',
            renderer: value => formatDate(value.createdTime),
            align: 'center',
        },
        {
            width: 3,
            header: 'Amount Paid',
            renderer: value => numberUtils.asCurrency(value.appliedPaymentAmount, '$0,0.00'),
            align: 'center',
        },
        {
            width: 3,
            header: 'Payment Number',
            dataKey: 'paymentNumber',
            align: 'center',
        },
        {
            width: 3,
            header: 'Transaction Status',
            dataKey: 'status',
            align: 'center',
            renderer: (value) => {
                const { status, gatewayResponseCode } = value;
                if (status === 'processed') {
                    return 'Complete';
                }
                let customMessage = '';
                if (gatewayResponseCode) {
                    const errorMessage = BillingConstants.RESPONSE_CODE_MESSAGES[gatewayResponseCode];
                    if (errorMessage) {
                        customMessage = `: ${errorMessage}`;
                    }
                }
                if (status.length) {
                    return status[0].toUpperCase() + status.slice(1) + customMessage;
                }
                return '-';
            },
        },
    ];

    if (paymentsEnabled) {
        historyColumnConfig.splice(1, 0, {
            width: 3,
            header: 'Payment Method',
            align: 'center',
            renderer: billingUtils.formatPaymentMethod,
        });
        historyColumnConfig.push({
            width: 3,
            header: 'Paid By',
            align: 'center',
            renderer: value => (value.email ? value.email : '-'),
        });
    }

    const handleEditClick = (accountNumber) => {
        const url = `${paths.billingPage}/account/${accountNumber}`;
        const target = `${url}?prev=invoice&invoiceNumber=${match.params.invoiceId}`;
        window.router.transitionTo(target);
    };

    React.useEffect(() => {
        dispatch(BillingActions.getInvoice(match.params.invoiceId));
        return () => {
            dispatch(BillingActions.clearCurrentInvoice());
        };
    }, []);

    const handlePayInfo = () => dispatch(ModalActions.showModal({
        modalType: ModalConstants.PAYMENT_INFO_MODAL,
    }));

    const renderPaymentHistory = () => { // eslint-disable-line no-unused-vars
        if (currentInvoice.payments.length === 0) return null;
        return (
            <StyledContainer className="InvoicePage-card" borderFull={true}>
                <Text className="InvoicePage-card-heading" fontType="h4">
                    Payment History
                </Text>
                <TableMFH
                    columnConfig={historyColumnConfig}
                    data={currentInvoice.payments.slice().sort((a, b) => b.createdTime - a.createdTime)}
                />
            </StyledContainer>
        );
    };

    const renderInvoiceDetails = () => (
        <Container className="InvoicePage-container">
            <StyledContainer className="InvoicePage-card" borderFull={true}>
                <Text className="InvoicePage-card-heading" fontType="h4">
                    Account summary
                </Text>
                <Row>
                    <Col md={12} lg={4}>
                        <StyledSummaryBox>
                            <Text fontType="h6" as="h6">
                                Current invoice amount
                            </Text>
                            <Text fontType="h4">
                                {numberUtils.asCurrency(currentInvoice.amount, '$0,0.00')}
                            </Text>
                        </StyledSummaryBox>
                    </Col>
                    <Col md={12} lg={4}>
                        <StyledSummaryBox>
                            <Text fontType="h6" as="h6">
                                Current balance
                            </Text>
                            <Text fontType="h4">
                                {numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}
                            </Text>
                        </StyledSummaryBox>
                    </Col>
                    <Col md={12} lg={4}>
                        <StyledSummaryBox>
                            <Text fontType="h6" as="h6">
                                Total account balance
                            </Text>
                            <Text fontType="h4">
                                {numberUtils.asCurrencyWithDefault(currentInvoice.totalAccountBalance, '-')}
                            </Text>
                        </StyledSummaryBox>
                    </Col>
                </Row>
            </StyledContainer>
            {renderPaymentHistory()}
            {currentInvoiceLineItems.length > 0 && (
                <StyledContainer className="InvoicePage-card" borderFull={true}>
                    <Text className="InvoicePage-card-heading" fontType="h4">
                        Invoice details
                    </Text>
                    <TableMFH
                        onSaveFilters={newFilters => dispatch(BillingActions.setInvoiceFilters(newFilters))}
                        columnConfig={columnConfig}
                        data={currentInvoiceLineItems}
                        FilterComponent={BillingFilter}
                        filters={currentInvoiceFilters}
                        isMobile={isScreenMobile}
                        idKey="invoice-line-items"
                    />
                    <Spacer paddingBottom="lg" />
                </StyledContainer>
            )}
        </Container>
    );

    const handleDownloadClick = () => {
        const invoiceNumbers = [];
        invoiceNumbers.push(match.params.invoiceId);
        dispatch(BillingActions.downloadInvoices(invoiceNumbers));
    };

    const handleDownloadCsv = () => {
        const exporter = isSingleFamilyLegacy ? BillingActions.downloadSingleFamilyLegacyCsv :
            BillingActions.downloadRocketshipCsv;
        dispatch(exporter());
    };

    const handlePayInvoiceClick = (invoiceId) => {
        window.router.transitionTo(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
    };

    const canDownloadCsv = currentInvoice &&
        (currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_DAILY_LISTINGS
            || currentInvoice.invoiceType === INVOICE_TYPES.SINGLE_FAMILY_LEGACY) &&
        currentInvoiceLineItems.length > 0;

    const renderHeader = () => (
        <React.Fragment>
            <div>
                {paymentsEnabled ?
                    (
                        <Text as="p" fontType="finePrint">
                            *Only credit card payment is supported for online payments right now.
                            For other types of payments, such as check, please follow the instructions
                            {' '}
                            <Anchor onClick={handlePayInfo}>here</Anchor>
                        </Text>
                    ) : (
                        <Text as="p" fontType="finePrint">
                            {BillingConstants.BILLING_INFO_FOOTNOTE}
                        </Text>
                    )
                }
            </div>
            <div>
                <Text className="InvoicePage-heading" fontType="h4">
                    {currentInvoice.accountName}
                </Text>
                <Spacer marginLeft="sm" as="span">
                    <TextButton onClick={handleDownloadClick}>
                        Download PDF
                    </TextButton>
                </Spacer>
                {canDownloadCsv && (
                    <Spacer marginLeft="sm" as="span">
                        <TextButton onClick={handleDownloadCsv}>
                            Download CSV
                        </TextButton>
                    </Spacer>
                )}
            </div>
            <Row>
                <Col md={12} lg={6}>
                    <Spacer paddingBottom="sm">
                        <Flex display="flex" justifyContent="space-between">
                            <Text fontType="bodyHeading">Invoice number</Text>
                            <Text>{match.params.invoiceId}</Text>
                        </Flex>
                    </Spacer>
                    <Spacer paddingBottom="sm">
                        <Flex display="flex" justifyContent="space-between">
                            <Text fontType="bodyHeading">Invoice date</Text>
                            <Text>{formatDate(currentInvoice.invoiceDate)}</Text>
                        </Flex>
                    </Spacer>
                    <Spacer paddingBottom="sm">
                        <Flex display="flex" justifyContent="space-between">
                            <Text fontType="bodyHeading">Service period</Text>
                            <Text>
                                {billingUtils.calculateServicePeriod(currentInvoice.invoiceItems)}
                            </Text>
                        </Flex>
                    </Spacer>
                    <Spacer paddingBottom="sm">
                        <Flex display="flex" justifyContent="space-between">
                            <Text fontType="bodyHeading">Last updated</Text>
                            <Text>
                                {billingUtils.formatZuoraRefresh(currentInvoice.lastTimeSyncedMs)}
                            </Text>
                        </Flex>
                    </Spacer>
                </Col>
                {currentInvoice.balance > 0 && !paymentsEnabled
                    && (
                        <Col className="InvoicePage-balance-details" md={12} lg={6}>
                            <Text as="p">
                                {`Balance due by ${(new Date(currentInvoice.dueDate)).toLocaleDateString()}`}
                            </Text>
                            <Text className="InvoicePage-amount-due" as="p">
                                {numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}
                            </Text>
                            <div
                                onClick={handlePayInfo}
                                type="body"
                                className="Utils-primary Invoice-Page-how-to-pay"
                            >
                                How to pay?
                            </div>
                        </Col>
                    )}
                {paymentsEnabled
                    && (
                        <Col className="InvoicePage-balance-details" xs={12} md={6}>
                            {currentInvoice.balance > 0 && (
                                <Text as="p">
                                    {currentInvoice.accountAutopay
                                        ? `Next payment date: ${billingUtils.getNextAutoPaymentDate()}`
                                        : `Balance due by ${(new Date(currentInvoice.dueDate)).toLocaleDateString()}`
                                    }
                                </Text>
                            )}
                            <Text as="p">
                                Auto Pay:
                                {' '}
                                <b>{currentInvoice.accountAutopay ? 'ON' : 'OFF'}</b>
                                {' '}
                                {currentInvoice.canMakePayments && (
                                    <TextButton onClick={() => handleEditClick(currentInvoice.accountNumber)}>
                                        Edit
                                    </TextButton>
                                )}
                            </Text>
                            {currentInvoice.balance > 0 && (
                                <Text className="InvoicePage-amount-due" as="p">
                                    {numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}
                                </Text>
                            )}
                            {currentInvoice.canMakePayments && currentInvoice.balance > 0 && (
                                <React.Fragment>
                                    <Spacer marginTop="sm" />
                                    <Button
                                        buttonType="primary"

                                        onClick={() => handlePayInvoiceClick(currentInvoice.invoiceNumber)}
                                    >
                                        Make a payment
                                    </Button>
                                </React.Fragment>
                            )}
                        </Col>
                    )}
            </Row>
        </React.Fragment>
    );

    const renderLoadingHeader = () => (
        <React.Fragment>
            <Row>
                <Col xs={8}>
                    <Text className="InvoicePage-heading" fontType="h4">
                        {match.params.invoiceId}
                    </Text>
                </Col>
            </Row>
            {currentInvoiceLoadingState !== LoadingConstants.ERROR && (
                <Row>
                    <Col>
                        <SpinnerContainer className="InvoicePage-spinner" />
                    </Col>
                </Row>
            )}
        </React.Fragment>
    );

    const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <PermissionCheck route={BILLING_PAGE}>
                <div className="InvoicePage">
                    <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                    <Container className="InvoicePage-container">
                        <PageBreadcrumb
                            backButton={(
                                <TextButton
                                    as={Link}
                                    to={queryParams.prev === 'paid'
                                        ? '/rental-manager/billing/paid' : '/rental-manager/billing/due'}
                                >
                                    Back
                                </TextButton>
                            )}
                        />
                        <StyledContainer className="InvoicePage-card InvoicePage-info-row">
                            {isLoaded ? renderHeader() : renderLoadingHeader()}
                        </StyledContainer>
                    </Container>
                    {currentInvoiceErrorMessage && (
                        <Container>
                            <BillingError errorMsg={currentInvoiceErrorMessage} />
                        </Container>
                    )}
                    {isLoaded && renderInvoiceDetails()}
                </div>
            </PermissionCheck>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoice: BillingSelectors.makeCurrentInvoiceSelector(state),
    currentInvoiceLineItems: BillingSelectors.makeCurrentInvoiceLineItems(state),
    currentInvoiceLoadingState: BillingSelectors.makeCurrentInvoiceLoadingState(state),
    currentInvoiceErrorMessage: BillingSelectors.makeCurrentInvoiceErrorMessage(state),
    currentInvoiceFilters: BillingSelectors.makeCurrentInvoiceFilters(state),
    downloadState: BillingSelectors.makeInvoicesDownloadState(state),
    downloadErrorMessage: BillingSelectors.makeInvoicesDownloadErrorMessage(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    user: UserSelectors.makeUser(state),
});

InvoicePage.propTypes = propTypes;
InvoicePage.defaultProps = defaultProps;

export { InvoicePage };
export default withErrorBoundary(connect(mapStateToProps)(InvoicePage));
